import {
	ConfigurationSelectorSkeleton,
	CorrespondingTableBasesTops,
	ProductGallery,
	ProductInformationGrid,
	QuickShipComponent,
	RelatedResourcesCardGrid,
	dataLayerPush,
	getResourceCardProps,
	useEnvironmentContext,
	useRelatedResourcesByProductCategory,
	useResourceContext,
	type CtaClickEvent,
	type ThreeDViewerActivatedEvent,
} from '@ds-mgnl/marketing';
import { SkeletonText, Table } from '@dsx/react';
import type { CSSProperties } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { groupAttributes } from '../../../components/ProductDetail';
import {
	buildCtaNavigation,
	pushFileDownloadEvent,
	pushProductImageUpdate,
	pushSelectItemOption,
} from '../../../components/ProductDetail/productAnalytics';
import { CallToAction } from '../../../components/ProductDetail/CallToAction/CallToAction';
import { FabricPreviewPicker } from '../../../components/ProductDetail/FabricPreviewPicker';
import {
	FamilyVariantPicker,
	type ProductVariantSelection,
} from '../../../components/ProductDetail/FamilyVariantPicker';
import { useCorrespondingProducts } from '../../../hooks/useCorrespondingProducts';
import { useDimensionAssetMap } from '../../../hooks/useDimensionAssetMap';
import { useFamilyAssetMap } from '../../../hooks/useFamilyAssetMap';
import { useProductMaterialAssetMap } from '../../../hooks/useProductMaterialAssetMap';
import { useProductPageViewAnalytics } from '../../../hooks/useProductPageViewAnalytics';
import {
	buildProduct3dConfigFromModelUrl,
	getProductById,
	type ProductAssetItem,
	type ProductResponse,
} from '../../../services/productApi';
import { getStoreProductUrl } from './getStoreProductUrl';
import { ModelViewer } from './ModelViewer';
import type { MaterialSlot } from '@ds-mgnl/marketing/model-viewer';
import {
	buildModelMaterials,
	firstResolvingFabricValueSlug,
	hasSelectableSurface,
	materialAttributeSelections,
	modelHasDimensionSlot,
	partitionAttributes,
	productAttributeToSelectedGaAttributeKey,
} from './pdpHelpers';
import { preloadModelAssets } from './preloadModelAssets';
import styles from './ProductDetailPage.module.css';

interface ProductDetailPageProps {
	product: ProductResponse | null;
	loading?: boolean;
}

const FABRIC_PREVIEW_SLUG = 'seat-fabric';

const FABRIC_QUERY_PARAM = 'fabric';

const BACK_FABRIC_QUERY_PARAM = 'backFabric';

const HIDDEN_DISCOVERY_STYLE: CSSProperties = {
	position: 'fixed',
	left: '-9999px',
	top: 0,
	width: '2px',
	height: '2px',
	overflow: 'hidden',
	pointerEvents: 'none',
};

export function ProductDetailPage({
	product,
	loading = false,
}: ProductDetailPageProps) {
	const {
		productCards,
		showsTableTops,
		isLoading: correspondingLoading,
		hasRelationships,
		totalCount,
		onPageChange,
		onFilterChange,
		filterOptions,
	} = useCorrespondingProducts(product);

	const navigate = useNavigate();

	const [familyModel, setFamilyModel] = useState<{
		familyId: string;
		modelUrl: string;
	} | null>(null);

	useEffect(() => {
		const familyId = product?.family?.id;
		const ownModelUrl = product?.config3d?.modelUrl;
		if (familyId && ownModelUrl) {
			setFamilyModel((prev) =>
				prev?.familyId === familyId && prev.modelUrl === ownModelUrl
					? prev
					: { familyId, modelUrl: ownModelUrl },
			);
		}
	}, [product?.config3d?.modelUrl, product?.family?.id]);

	const config3d = useMemo(() => {
		const own = product?.config3d;
		if (own) return own;

		const familyId = product?.family?.id;
		if (familyId && familyModel?.familyId === familyId && familyModel.modelUrl) {
			return buildProduct3dConfigFromModelUrl(familyModel.modelUrl);
		}
		return undefined;
	}, [product?.config3d, product?.family?.id, familyModel]);

	const location = useLocation();
	const viaConfigurator =
		(location.state as { viaConfigurator?: boolean } | null)?.viaConfigurator ??
		false;

	const { map: familyAssetMap, isLoading: familyMapLoading } =
		useFamilyAssetMap(product?.family?.id);

	const attributeSelections = useMemo(
		() => (product?.family?.id ? {} : materialAttributeSelections(product)),
		[product],
	);
	const { map: attributeAssetMap } = useProductMaterialAssetMap(
		product?.family?.id ? undefined : product?.id,
		attributeSelections,
	);
	const materialAssetMap = familyAssetMap ?? attributeAssetMap;

	const dimSlug =
		!product?.isQuickShip && !!product?.attributes?.['Fabric Grade']
			? FABRIC_PREVIEW_SLUG
			: undefined;
	// * The fabric preview only renders on non-quick-ship pages (see dimSlug).
	const { dimension: fabricDimension } = useDimensionAssetMap(dimSlug);
	const showFabricPreview = !!fabricDimension;

	const [discoveredSlots, setDiscoveredSlots] = useState<MaterialSlot[] | null>(
		null,
	);
	useEffect(() => {
		setDiscoveredSlots(null);
	}, [config3d?.modelUrl]);

	const showBackFabricPreview =
		showFabricPreview && modelHasDimensionSlot(discoveredSlots, 'back-fabric');

	const [searchParams, setSearchParams] = useSearchParams();
	const fabricValueSlugs = fabricDimension
		? Object.keys(fabricDimension.values)
		: [];
	const paramFabric = searchParams.get(FABRIC_QUERY_PARAM) ?? undefined;
	const defaultFabricSlug =
		firstResolvingFabricValueSlug(fabricDimension) ?? fabricValueSlugs[0];
	const selectedFabricSlug =
		paramFabric && fabricDimension?.values[paramFabric]
			? paramFabric
			: defaultFabricSlug;
	const paramBackFabric =
		searchParams.get(BACK_FABRIC_QUERY_PARAM) ?? undefined;
	const selectedBackFabricSlug =
		paramBackFabric && fabricDimension?.values[paramBackFabric]
			? paramBackFabric
			: defaultFabricSlug;

	const [activeGalleryIndex, setActiveGalleryIndex] = useState(0);

	const modelMaterials = useMemo(() => {
		if (!config3d) return undefined;
		const fabricOverride =
			fabricDimension && selectedFabricSlug
				? {
						dimension: fabricDimension,
						seatValueSlug: selectedFabricSlug,
						backValueSlug: showBackFabricPreview
							? selectedBackFabricSlug
							: undefined,
					}
				: undefined;
		return buildModelMaterials(
			config3d,
			materialAssetMap,
			product?.id,
			fabricOverride,
			discoveredSlots,
		);
	}, [
		config3d,
		materialAssetMap,
		product?.id,
		fabricDimension,
		selectedFabricSlug,
		selectedBackFabricSlug,
		showBackFabricPreview,
		discoveredSlots,
	]);

	useEffect(() => {
		preloadModelAssets(config3d);
	}, [config3d]);

	useEffect(() => {
		if (!showFabricPreview || !defaultFabricSlug) return;

		const hasValidFabricParam = !!(
			paramFabric && fabricDimension?.values[paramFabric]
		);
		const needsBackFabricParam =
			showBackFabricPreview &&
			!!selectedBackFabricSlug &&
			!(paramBackFabric && fabricDimension?.values[paramBackFabric]);

		if (hasValidFabricParam && !needsBackFabricParam) return;

		const next = new URLSearchParams(searchParams);
		if (!hasValidFabricParam) next.set(FABRIC_QUERY_PARAM, defaultFabricSlug);
		if (needsBackFabricParam) {
			next.set(BACK_FABRIC_QUERY_PARAM, selectedBackFabricSlug);
		}
		setSearchParams(next, { replace: true });
	}, [
		showFabricPreview,
		showBackFabricPreview,
		defaultFabricSlug,
		selectedBackFabricSlug,
		paramFabric,
		paramBackFabric,
		fabricDimension,
		searchParams,
		setSearchParams,
	]);

	// Build gallery items: product images + 3D model (if configured)
	const galleryItems = useMemo(() => {
		const items: { type: 'image'; src: string; alt?: string }[] = (
			product?.images ?? []
		).map((img) => ({
			type: 'image' as const,
			src: img.contentUrl ?? '',
			alt: img.description || product?.name || '',
		}));

		if (config3d) {
			return [
				...items,
				{
					type: 'model' as const,
					ModelViewerComponent: ModelViewer,
					viewerProps: {
						modelUrl: config3d.modelUrl,
						materials: modelMaterials,
						backgroundColor: 0xffffff,
						cacheEnabled: true,
						onSlotsDiscovered: setDiscoveredSlots,
					},
				},
			];
		}

		return items;
	}, [product, config3d, modelMaterials]);

	const modelItemIndex = galleryItems.findIndex(
		(item) => item.type === 'model',
	);
	const isModelPresent = modelItemIndex !== -1;
	const isModelActive = activeGalleryIndex === modelItemIndex;
	const imageCount = product?.images?.length ?? 0;

	const preferImageOverBlankModel =
		isModelPresent &&
		!product?.isQuickShip &&
		imageCount > 0 &&
		!hasSelectableSurface(modelMaterials);

	const shouldDiscoverSlotsInBackground =
		!!config3d && showFabricPreview && !discoveredSlots && !isModelActive;

	type GalleryPosition =
		| { kind: 'model' }
		| { kind: 'last-image' }
		| { kind: 'image'; index: number };
	// * The semantic gallery position (which slot the user is viewing) is held
	//   in a ref, not state: it is never rendered, only read to restore the
	//   equivalent slot after navigating to a sibling product. Tracking it here
	//   keeps all gallery state mutation inside the effect below rather than
	//   during render.
	const galleryRef = useRef<{
		productId: string | undefined;
		position: GalleryPosition;
	}>({ productId: product?.id, position: { kind: 'image', index: 0 } });

	useEffect(() => {
		const prev = galleryRef.current;

		if (prev.productId !== product?.id) {
			// Product changed: restore the equivalent slot in the new product's
			// gallery (e.g. stay on the 3D model across a variant switch).
			const { position } = prev;
			let restored = activeGalleryIndex;
			if (galleryItems.length > 0) {
				if (preferImageOverBlankModel) {
					restored = 0;
				} else if (viaConfigurator && isModelPresent) {
					// Selection came from the configurator and this product has a
					// 3D model: surface it so the picked options are visible.
					restored = modelItemIndex;
				} else if (position.kind === 'model' && isModelPresent) {
					restored = modelItemIndex;
				} else if (position.kind === 'last-image' && imageCount > 0) {
					restored = imageCount - 1;
				} else if (position.kind === 'image') {
					const maxImageIdx = Math.max(0, imageCount - 1);
					restored = Math.min(position.index, maxImageIdx);
				}
			}
			galleryRef.current = { productId: product?.id, position };
			// This runs only on product navigation (not a hot path), so the
			// cascading-render cost the rule guards against is negligible; the
			// alternative (adjusting state during render) is disallowed here by
			// team direction.
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setActiveGalleryIndex(restored);
			return;
		}

		// Same product: remember the current selection's semantic position so it
		// can be restored on the next navigation.
		let position: GalleryPosition;
		if (isModelPresent && activeGalleryIndex === modelItemIndex) {
			position = { kind: 'model' };
		} else if (imageCount > 0 && activeGalleryIndex === imageCount - 1) {
			position = { kind: 'last-image' };
		} else {
			position = { kind: 'image', index: activeGalleryIndex };
		}
		galleryRef.current = { productId: product?.id, position };
	}, [
		product?.id,
		activeGalleryIndex,
		galleryItems.length,
		modelItemIndex,
		isModelPresent,
		imageCount,
		viaConfigurator,
		preferImageOverBlankModel,
	]);

	const { dimensionRows, otherRows, hasDimensions, hasOther } = useMemo(() => {
		const { dimensions, other } = partitionAttributes(
			product?.attributes ?? {},
		);
		const otherRows = groupAttributes(other);

		const supplierRows = (product?.suppliers ?? [])
			.filter((supplier) => !!supplier.id)
			.map((supplier, index) => ({
				id: `supplier_${supplier.id}_${index}`,
				label: 'Supplier ID',
				value: supplier.id as string,
			}));
		otherRows.push(...supplierRows);

		return {
			dimensionRows: groupAttributes(dimensions),
			otherRows,
			hasDimensions: Object.keys(dimensions).length > 0,
			hasOther: otherRows.length > 0,
		};
	}, [product?.attributes, product?.suppliers]);

	const familyPickerData =
		familyAssetMap &&
		product?.id &&
		familyAssetMap.products[product.id] &&
		(Object.keys(familyAssetMap.assets.dimensions).length > 0 ||
			Object.keys(familyAssetMap.variants ?? {}).length > 0)
			? { map: familyAssetMap, currentProductId: product.id }
			: null;

	// Resource context is used to generate the correct URLs for related resources.
	const { getResourceUrl, getHubUrl } = useResourceContext();
	const { damHost } = useEnvironmentContext();

	// Fetch related resources for the product category (Furnishings).
	const { resources, loading: relatedResourcesLoading } =
		useRelatedResourcesByProductCategory({
			productCategoryPath: '/product-categories/Furnishings',
		});

	// Memoize the related resource view props to avoid unnecessary re-renders.
	const relatedResourceViewProps = useMemo(() => {
		return getResourceCardProps({
			resources: resources,
			getResourceUrl,
			getHubUrl,
			damHost,
		});
	}, [resources, getResourceUrl, getHubUrl, damHost]);

	const onSelectVariant = async ({
		productId: nextProductId,
		optionName,
		optionValue,
	}: ProductVariantSelection) => {
		const nextProduct = await getProductById(nextProductId);

		if (nextProduct) {
			pushSelectItemOption(nextProduct, {
				optionType: optionName,
				optionValue,
				viewerActive: isModelActive,
			});

			if (isModelActive) {
				pushProductImageUpdate(nextProduct, {
					optionType: optionName,
					optionValue,
					imageContext: '3d_viewer',
				});
			}
		}
	};

	const pushFabricSelectionEvents = (optionType: string, valueSlug: string) => {
		if (!fabricDimension) return;

		if (fabricDimension) {
			const optionValue =
				fabricDimension.values[valueSlug]?.display ?? valueSlug;
				
			const fabricProduct = {
				id: product?.manufacturerProductId,
				name: product?.name,
			};

			pushSelectItemOption(fabricProduct, {
				optionType: fabricDimension.display,
				optionValue,
				viewerActive: isModelPresent,
			});

			if (isModelPresent) {
				pushProductImageUpdate(fabricProduct, {
					optionType: fabricDimension.display,
					optionValue,
					imageContext: '3d_viewer',
				});
			}
		}
	}

	const onSelectFabric = (valueSlug: string) => {
		if (isModelPresent) {
			setActiveGalleryIndex(modelItemIndex);
		}

		pushFabricSelectionEvents(fabricDimension?.display ?? 'Seat Fabric', valueSlug);

		const next = new URLSearchParams(searchParams);
		next.set(FABRIC_QUERY_PARAM, valueSlug);
		setSearchParams(next, { replace: true });
	};

	const onSelectBackFabric = (valueSlug: string) => {
		if (isModelPresent) {
			setActiveGalleryIndex(modelItemIndex);
		}

		pushFabricSelectionEvents('Back Fabric', valueSlug);

		const next = new URLSearchParams(searchParams);
		next.set(BACK_FABRIC_QUERY_PARAM, valueSlug);
		setSearchParams(next, { replace: true });
	};

	const onRequestQuote = () => {
		if (!product?.id) return;

		const configAttributes = {
			...materialAssetMap?.products[product.id],
			...(showFabricPreview && selectedFabricSlug
				? { [FABRIC_PREVIEW_SLUG]: selectedFabricSlug }
				: {}),
			...(showBackFabricPreview && selectedBackFabricSlug
				? { 'back-fabric': selectedBackFabricSlug }
				: {}),
		};
		const selectedAttributes = Object.entries(configAttributes ?? {}).reduce<
			Record<`selected_${string}`, string>
		>((acc, [key, value]) => {
			if (value == null) return acc;

			const selectedKey = productAttributeToSelectedGaAttributeKey(key);
			if (!selectedKey) return acc;

			acc[selectedKey] = String(value);
			return acc;
		}, {});

		const destination = buildCtaNavigation({
			path: '/contact/request-quote',
			params: {
				...configAttributes,
				productId: product.id,
				productName: product.name,
				manufacturerNumber: product.manufacturerProductId,
				dsNumber: product.manufacturerCode,
			},
			ctaLabel: 'Request a Quote',
			ctaLocation: 'pdp_configurator',
			product,
			extraEventFields: selectedAttributes,
		});

		navigate(destination);
	};

	const onSelectActiveGalleryIndex = (index: number) => {
		setActiveGalleryIndex(index);

		if (index === modelItemIndex && isModelPresent) {
			const threeDViewerActivatedEvent: ThreeDViewerActivatedEvent = {
				event: '3d_viewer_activated',
				product_id: product?.id ?? '',
				product_name: product?.name ?? '',
			};
			dataLayerPush(threeDViewerActivatedEvent);
		}
	};

	useProductPageViewAnalytics(product);

	function onFileDownload(
		asset: ProductAssetItem,
		fileType: string = '',
	): void {
		if (!asset || !product) {
			return;
		}
		pushFileDownloadEvent(product, asset, fileType);
	}

	function onQuickShipClick(
		event: React.MouseEvent<HTMLElement, MouseEvent>,
	): void {
		if (!product?.id) return;

		const quickShipUrl = getStoreProductUrl(product.id);
		const ctaClickEvent: CtaClickEvent = {
			event: 'cta_click',
			cta_label: event.currentTarget.textContent,
			cta_location: 'pdp_configurator',
			product_id: product.id ?? '',
			product_name: product.name ?? '',
			cta_destination: quickShipUrl,
			cta_destination_site: 'ecommerce',
			product_type: 'quick_ship',
		};
		dataLayerPush(ctaClickEvent);
	}

	// Lead time drives the grid's "Ships in N Days" pill; empty string hides it
	// (see ProductInformationGrid). The grid pluralises and handles "0" itself.
	const leadTimeDays = product?.availability?.leadTimeDays;
	const shippingInfo = leadTimeDays != null ? String(leadTimeDays) : '';

	return (
		<div className={styles['product-detail-container']}>
			<div className={styles['main-information-container']}>
				<div className={styles['image-carousel-container']}>
					<ProductGallery
						items={galleryItems}
						selectedIndex={activeGalleryIndex}
						onSelectIndex={onSelectActiveGalleryIndex}
					/>
					{shouldDiscoverSlotsInBackground && config3d && (
						// Off-screen, non-interactive, and unmounted the moment slots
						// arrive — see shouldDiscoverSlotsInBackground.
						<div aria-hidden style={HIDDEN_DISCOVERY_STYLE}>
							<ModelViewer
								modelUrl={config3d.modelUrl}
								cacheEnabled
								onSlotsDiscovered={setDiscoveredSlots}
							/>
						</div>
					)}
				</div>
				<div className={styles['product-information-container']}>
					<ProductInformationGrid
						productName={product?.name ?? product?.family?.name ?? ''}
						manufacturer={product?.manufacturerName ?? ''}
						manfNumber={product?.manufacturerProductId ?? ''}
						price={
							product?.price != null
								? `${product.price.toFixed(2)}`
								: (product?.priceRange ?? '')
						}
						shippingInfo={shippingInfo}
						loading={loading}
					/>
					{loading || familyMapLoading ? (
						<ConfigurationSelectorSkeleton />
					) : (
						<>
							{familyPickerData && (
								<FamilyVariantPicker
									{...familyPickerData}
									onSelect={onSelectVariant}
								/>
							)}
							{showFabricPreview && (
								<FabricPreviewPicker
									dimension={fabricDimension}
									selectedValueSlug={selectedFabricSlug}
									onSelect={onSelectFabric}
									selectorLabel={showBackFabricPreview ? 'Seat Fabric' : undefined}
								/>
							)}
							{showBackFabricPreview && (
								<FabricPreviewPicker
									dimension={fabricDimension}
									selectedValueSlug={selectedBackFabricSlug}
									onSelect={onSelectBackFabric}
									selectorLabel="Back Fabric"
								/>
							)}
						</>
					)}
					<CallToAction
						product={product}
						loading={loading}
						onRequestQuote={onRequestQuote}
						onFileDownload={onFileDownload}
					/>
					{(loading || product?.isQuickShip) && (
						<QuickShipComponent
							buttonHref={getStoreProductUrl(product?.id)}
							loading={loading}
							onClick={onQuickShipClick}
						/>
					)}
				</div>
			</div>
			<div className={styles['specifications-container']}>
				{loading && (
					<>
						<div className={styles['specifications-table']}>
							<SkeletonText lines={5} />
						</div>
						<div className={styles['specifications-table']}>
							<SkeletonText lines={5} />
						</div>
					</>
				)}
				{!loading && hasDimensions && (
					<div className={styles['specifications-table']}>
						<Table
							columns={[
								{ heading: 'Dimensions', key: 'label' },
								{ heading: '', key: 'value' },
							]}
							rows={dimensionRows}
							rowKey={(row) => row.id}
							onSelect={() => {}}
							outline="none"
						/>
					</div>
				)}
				{!loading && hasOther && (
					<div className={styles['specifications-table']}>
						<Table
							columns={[
								{ heading: 'Product Details', key: 'label' },
								{ heading: '', key: 'value' },
							]}
							rows={otherRows}
							rowKey={(row) => row.id}
							onSelect={() => {}}
							outline="none"
						/>
					</div>
				)}
			</div>
			{(loading || correspondingLoading || hasRelationships) && (
				<CorrespondingTableBasesTops
					productCards={productCards}
					isTableTop={showsTableTops}
					totalCount={totalCount}
					onPageChange={onPageChange}
					onFilterChange={onFilterChange}
					filterOptions={filterOptions}
					loading={loading || correspondingLoading}
				/>
			)}
			{(relatedResourcesLoading || resources.length > 0) && (
				<RelatedResourcesCardGrid
					relatedResources={relatedResourceViewProps}
					loading={relatedResourcesLoading}
				/>
			)}
		</div>
	);
}
